import React, { useState, useEffect } from 'react';
import { message, Modal, Tabs, Button } from 'antd';
import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { DynamicForm, DynamicFormList, FormData } from '../../components/DynamicForm';
import { deviceFormConfig } from './config';
import { v4 as uuidv4 } from 'uuid';

// 模拟API服务
const mockApi = {
  // 获取设备列表
  getDevices: (): Promise<FormData[]> => {
    return new Promise((resolve) => {
      // 从localStorage获取数据，如果没有则返回空数组
      const storedData = localStorage.getItem('deviceData');
      const devices = storedData ? JSON.parse(storedData) : [];
      setTimeout(() => resolve(devices), 500); // 模拟网络延迟
    });
  },
  
  // 添加设备
  addDevice: (data: Record<string, any>): Promise<FormData> => {
    return new Promise((resolve) => {
      // 从localStorage获取现有数据
      const storedData = localStorage.getItem('deviceData');
      const devices = storedData ? JSON.parse(storedData) : [];
      
      // 创建新设备记录
      const newDevice: FormData = {
        id: uuidv4(),
        formId: deviceFormConfig.id,
        data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // 添加到数组并保存回localStorage
      devices.push(newDevice);
      localStorage.setItem('deviceData', JSON.stringify(devices));
      
      setTimeout(() => resolve(newDevice), 500); // 模拟网络延迟
    });
  },
  
  // 更新设备
  updateDevice: (id: string, data: Record<string, any>): Promise<FormData> => {
    return new Promise((resolve, reject) => {
      // 从localStorage获取现有数据
      const storedData = localStorage.getItem('deviceData');
      const devices: FormData[] = storedData ? JSON.parse(storedData) : [];
      
      // 查找要更新的设备
      const index = devices.findIndex(device => device.id === id);
      if (index === -1) {
        return reject(new Error('设备不存在'));
      }
      
      // 更新设备数据
      const updatedDevice: FormData = {
        ...devices[index],
        data,
        updatedAt: new Date().toISOString()
      };
      
      devices[index] = updatedDevice;
      localStorage.setItem('deviceData', JSON.stringify(devices));
      
      setTimeout(() => resolve(updatedDevice), 500); // 模拟网络延迟
    });
  },
  
  // 删除设备
  deleteDevice: (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 从localStorage获取现有数据
      const storedData = localStorage.getItem('deviceData');
      const devices: FormData[] = storedData ? JSON.parse(storedData) : [];
      
      // 查找要删除的设备
      const index = devices.findIndex(device => device.id === id);
      if (index === -1) {
        return reject(new Error('设备不存在'));
      }
      
      // 删除设备
      devices.splice(index, 1);
      localStorage.setItem('deviceData', JSON.stringify(devices));
      
      setTimeout(() => resolve(), 500); // 模拟网络延迟
    });
  }
};

const DeviceManagement: React.FC = () => {
  const [devices, setDevices] = useState<FormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentDevice, setCurrentDevice] = useState<FormData | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [activeTab, setActiveTab] = useState('list');
  
  // 加载设备列表
  const loadDevices = async () => {
    setLoading(true);
    try {
      const data = await mockApi.getDevices();
      setDevices(data);
    } catch (error) {
      message.error('加载设备列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // 初始加载
  useEffect(() => {
    loadDevices();
  }, []);
  
  // 处理添加设备
  const handleAdd = () => {
    setCurrentDevice(null);
    setFormMode('create');
    setModalVisible(true);
  };
  
  // 处理编辑设备
  const handleEdit = (record: FormData) => {
    setCurrentDevice(record);
    setFormMode('edit');
    setModalVisible(true);
  };
  
  // 处理查看设备
  const handleView = (record: FormData) => {
    setCurrentDevice(record);
    setFormMode('view');
    setModalVisible(true);
  };
  
  // 处理删除设备
  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await mockApi.deleteDevice(id);
      message.success('删除成功');
      loadDevices();
    } catch (error) {
      message.error('删除失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // 处理表单提交
  const handleFormFinish = async (values: Record<string, any>) => {
    setLoading(true);
    try {
      if (formMode === 'create') {
        await mockApi.addDevice(values);
        message.success('添加成功');
      } else if (formMode === 'edit' && currentDevice?.id) {
        await mockApi.updateDevice(currentDevice.id, values);
        message.success('更新成功');
      }
      
      setModalVisible(false);
      loadDevices();
    } catch (error) {
      message.error(formMode === 'create' ? '添加失败' : '更新失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // 处理表单取消
  const handleFormCancel = () => {
    setModalVisible(false);
  };
  
  // 处理添加新设备
  const handleAddNewDevice = () => {
    setActiveTab('form');
  };
  
  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1>设备管理</h1>
        <Link to="/dynamic-form-nav">
          <Button icon={<ArrowLeftOutlined />}>
            返回导航页面
          </Button>
        </Link>
      </div>
      
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'list',
            label: '设备列表',
            children: (
              <DynamicFormList
                config={deviceFormConfig}
                dataSource={devices}
                loading={loading}
                onAdd={handleAddNewDevice}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            )
          },
          {
            key: 'form',
            label: '添加设备',
            children: (
              <DynamicForm
                config={deviceFormConfig}
                onFinish={handleFormFinish}
                loading={loading}
                mode="create"
              />
            )
          }
        ]}
      />
      
      {/* 编辑/查看模态框 */}
      <Modal
        title={
          formMode === 'create' 
            ? '添加设备' 
            : formMode === 'edit' 
              ? '编辑设备' 
              : '查看设备'
        }
        open={modalVisible}
        onCancel={handleFormCancel}
        footer={null}
        width={800}
      >
        <DynamicForm
          config={deviceFormConfig}
          initialValues={currentDevice?.data}
          onFinish={handleFormFinish}
          onCancel={handleFormCancel}
          loading={loading}
          mode={formMode}
        />
      </Modal>
    </div>
  );
};

export default DeviceManagement; 